import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Button } from '@mui/material';

import { onboardingApi } from '@/core/api';

/**
 * A banner on the administrator's home when companies are waiting to be let in.
 *
 * The applications screen has no row in the navigation menu, because that menu
 * is rows in a database table rather than a file in this repository — adding
 * one is a migration and a deploy, and this needed to work today.
 *
 * That constraint produced the better answer anyway. An application is not
 * something you go looking for on a schedule; it is something that should find
 * you, because on the other end a company is sitting on a waiting screen unable
 * to work. So it announces itself on the first page every administrator sees,
 * and it is invisible on every other day.
 *
 * Silent on failure. A dashboard that renders an error where a banner would go,
 * every time this endpoint has a bad moment, teaches people to ignore the space
 * it occupies.
 */
export default function PendingApplications() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void onboardingApi
      .getApplications('SUBMITTED')
      .then((rows) => {
        if (!cancelled) setCount(rows.length);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (count === 0) return null;

  return (
    <Alert
      severity="warning"
      sx={{ mb: 3 }}
      action={
        <Button
          component={RouterLink}
          to="/configuration/applications"
          color="inherit"
          size="small"
          variant="outlined"
        >
          Review
        </Button>
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {count === 1 ? 'A company is waiting to be approved' : `${count} companies are waiting`}
      </AlertTitle>
      They have finished their application and cannot use iTow until somebody approves it.
    </Alert>
  );
}
